import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getVisibleRutas, isRutaRestricted } from '../lib/rutas';
import { DollarSign, TrendingUp, TrendingDown, Users, Calendar, Plus, Minus, X, Info, Clock, User, TriangleAlert as AlertTriangle, CircleCheck as CheckCircle, Search, Eye, FileText } from 'lucide-react';

interface Cashbox {
  id: string;
  ruta: number;
  unit_id: string;
  opened_at: string;
  closed_at?: string;
  opened_by_user_id: string;
  closed_by_user_id?: string;
  initial_cash_amount: number;
  status: 'open' | 'closed';
  mobile_start: boolean;
  final_cash_amount?: number;
}

interface CashMetrics {
  // Caixa Atual
  initialCash: number;
  newSales: number;
  renewedSales: number;
  totalSales: number;
  collections: number;
  income: number;
  expenses: number;
  withdrawals: number;
  availableCash: number;
  pendingVerification: number;
  totalLateFees: number;
  unallocatedLateFees: number;
  
  // Desempenho - Carteira
  portfolioFinal: number;
  portfolioVariation: number;
  portfolioInitial: number;
  portfolioCollected: number;
  portfolioFutureReceivable: number;
  portfolioOverdue: number;
  portfolioCurvas: number;
  
  // Desempenho - Recebimento
  intendedCollection: number;
  clientsToCollect: number;
  actualCollection: number;
  additionalCollection: number;
  compliance: number;
  unitCollectionPercentage: number;
  extraCollection: number;
  unscheduledClients: number;
  
  // Informações - Créditos
  creditsToCollect: number;
  creditsToCollectAmount: number;
  unscheduledCredits: number;
  unscheduledCreditsAmount: number;
  cancelledCredits: number;
  newCredits: number;
  activeCredits: number;
  
  // Informações - Movimento de Créditos
  paidCredits: number;
  unpaidCredits: number;
  synchronizedCredits: number;
  clientEfficiency: number;
}

interface Informacoes {
  sessao: {
    unidade: string;
    trabalhador: string;
    abertura: string;
    fechamento_ou_status: string;
  };
  a_receber: number;
  quitados: number;
  nao_programados: number;
  novos: number;
  ativos: number;
  movimento_creditos: {
    pagos: number;
    nao_pagaram: number;
    sincronizados: number;
  };
  eficiencia_clientes_percent: number;
}

interface SaleDetail {
  id: string;
  contract_id: string;
  client_name: string;
  contract_code: string;
  amount: number;
  created_at: string;
  seller_email: string;
  kind: 'new_sale' | 'renewed_sale';
}

interface PaymentDetail {
  id: string;
  client_name: string;
  contract_code: string;
  installment_number: number;
  amount: number;
  late_fee: number;
  registered_at: string;
  verified_at?: string;
  status: string;
}

interface IncomeDetail {
  id: string;
  description: string;
  amount: number;
  created_at: string;
  user_email: string;
}

interface LateFeeDetail {
  id: string;
  client_name: string;
  contract_code: string;
  amount: number;
  verified_at: string;
  allocated: boolean;
  allocated_to_client?: string;
  allocated_to_contract?: string;
}

interface ExpenseDetail {
  id: string;
  description: string;
  amount: number;
  created_at: string;
  user_email: string;
}

interface WithdrawalDetail {
  id: string;
  description: string;
  amount: number;
  created_at: string;
  user_email: string;
}

interface CashboxHistoryItem extends Cashbox {
  opened_by_user?: {
    email: string;
  };
  closed_by_user?: {
    email: string;
  };
}

interface CashboxSnapshot extends CashMetrics {
  id: string;
  cashbox_id: string;
  created_at: string;
}

export default function CaixaV3() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRuta, setSelectedRuta] = useState<number>(1);
  const [currentCashbox, setCurrentCashbox] = useState<Cashbox | null>(null);
  const [unitName, setUnitName] = useState<string>('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [cashboxHistory, setCashboxHistory] = useState<CashboxHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [initialCashAmount, setInitialCashAmount] = useState<string>('');
  const [finalCashAmount, setFinalCashAmount] = useState<string>('');
  const [opening, setOpening] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [expenseAmount, setExpenseAmount] = useState<string>('');
  const [expenseDescription, setExpenseDescription] = useState<string>('');
  const [withdrawalAmount, setWithdrawalAmount] = useState<string>('');
  const [withdrawalDescription, setWithdrawalDescription] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [allocationAmount, setAllocationAmount] = useState<string>('');
  const [selectedOverdueClient, setSelectedOverdueClient] = useState<any>(null);
  const [overdueClients, setOverdueClients] = useState<any[]>([]);
  const [loadingOverdueClients, setLoadingOverdueClients] = useState(false);
  const [allocating, setAllocating] = useState(false);

  const [showNewSalesModal, setShowNewSalesModal] = useState(false);
  const [showRenewedSalesModal, setShowRenewedSalesModal] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showLateFeesModal, setShowLateFeesModal] = useState(false);
  const [showExpensesListModal, setShowExpensesListModal] = useState(false);
  const [showWithdrawalsListModal, setShowWithdrawalsListModal] = useState(false);

  const [newSalesDetails, setNewSalesDetails] = useState<SaleDetail[]>([]);
  const [renewedSalesDetails, setRenewedSalesDetails] = useState<SaleDetail[]>([]);
  const [paymentsDetails, setPaymentsDetails] = useState<PaymentDetail[]>([]);
  const [incomeDetails, setIncomeDetails] = useState<IncomeDetail[]>([]);
  const [lateFeesDetails, setLateFeesDetails] = useState<LateFeeDetail[]>([]);
  const [expensesDetails, setExpensesDetails] = useState<ExpenseDetail[]>([]);
  const [withdrawalsDetails, setWithdrawalsDetails] = useState<WithdrawalDetail[]>([]);

  const [loadingDetails, setLoadingDetails] = useState(false);

  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<CashboxSnapshot | null>(null);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);

  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [loadingDebug, setLoadingDebug] = useState(false);

  const [cashMetrics, setCashMetrics] = useState<CashMetrics>({
    initialCash: 0,
    newSales: 0,
    renewedSales: 0,
    totalSales: 0,
    collections: 0,
    income: 0,
    expenses: 0,
    withdrawals: 0,
    availableCash: 0,
    pendingVerification: 0,
    totalLateFees: 0,
    unallocatedLateFees: 0,
    portfolioFinal: 0,
    portfolioVariation: 0,
    portfolioInitial: 0,
    portfolioCollected: 0,
    portfolioFutureReceivable: 0,
    portfolioOverdue: 0,
    portfolioCurvas: 0,
    intendedCollection: 0,
    clientsToCollect: 0,
    actualCollection: 0,
    additionalCollection: 0,
    compliance: 0,
    unitCollectionPercentage: 0,
    extraCollection: 0,
    unscheduledClients: 0,
    creditsToCollect: 0,
    creditsToCollectAmount: 0,
    unscheduledCredits: 0,
    unscheduledCreditsAmount: 0,
    cancelledCredits: 0,
    newCredits: 0,
    activeCredits: 0,
    paidCredits: 0,
    unpaidCredits: 0,
    synchronizedCredits: 0,
    clientEfficiency: 0
  });

  const [informacoes, setInformacoes] = useState<Informacoes>({
    sessao: {
      unidade: '',
      trabalhador: '',
      abertura: '',
      fechamento_ou_status: ''
    },
    a_receber: 0,
    quitados: 0,
    nao_programados: 0,
    novos: 0,
    ativos: 0,
    movimento_creditos: {
      pagos: 0,
      nao_pagaram: 0,
      sincronizados: 0
    },
    eficiencia_clientes_percent: 0
  });

  useEffect(() => {
    if (user) {
      fetchCashboxData();
      fetchUnitName();
    }
  }, [selectedRuta, user]);

  useEffect(() => {
    if (isRutaRestricted(user)) {
      const visible = getVisibleRutas(user);
      if (!visible.includes(selectedRuta)) setSelectedRuta(visible[0]);
    }
  }, [user]);

  useEffect(() => {
    if (showHistoryModal) {
      fetchCashboxHistory();
    }
  }, [showHistoryModal, selectedRuta, user]);

  const fetchUnitName = async () => {
    if (!user?.unit_id) return;
    
    try {
      const { data, error } = await supabase
        .from('units')
        .select('name')
        .eq('id', user.unit_id)
        .single();

      if (error) throw error;
      setUnitName(data?.name || 'Unidade');
    } catch (err) {
      console.error('Error fetching unit name:', err);
      setUnitName('Unidade');
    }
  };

  const fetchCashboxData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);

      // Get current open cashbox for the ruta
      const { data: openCashbox, error: cashboxError } = await supabase
        .from('cashboxes')
        .select('*')
        .eq('unit_id', user.unit_id)
        .eq('ruta', selectedRuta)
        .eq('status', 'open')
        .maybeSingle();

      if (cashboxError) throw cashboxError;

      setCurrentCashbox(openCashbox);

      if (openCashbox) {
        // Calculate metrics for open cashbox
        await calculateMetrics(openCashbox);
      } else {
        // Reset metrics when no open cashbox
        setCashMetrics({
          initialCash: 0,
          newSales: 0,
          renewedSales: 0,
          totalSales: 0,
          collections: 0,
          income: 0,
          expenses: 0,
          withdrawals: 0,
          availableCash: 0,
          pendingVerification: 0,
          totalLateFees: 0,
          unallocatedLateFees: 0,
          portfolioFinal: 0,
          portfolioVariation: 0,
          portfolioInitial: 0,
          portfolioCollected: 0,
          portfolioFutureReceivable: 0,
          portfolioOverdue: 0,
          portfolioCurvas: 0,
          intendedCollection: 0,
          clientsToCollect: 0,
          actualCollection: 0,
          additionalCollection: 0,
          compliance: 0,
          unitCollectionPercentage: 0,
          extraCollection: 0,
          unscheduledClients: 0,
          creditsToCollect: 0,
          creditsToCollectAmount: 0,
          unscheduledCredits: 0,
          unscheduledCreditsAmount: 0,
          cancelledCredits: 0,
          newCredits: 0,
          activeCredits: 0,
          paidCredits: 0,
          unpaidCredits: 0,
          synchronizedCredits: 0,
          clientEfficiency: 0
        });
      }

      // Update session info
      setInformacoes(prev => ({
        ...prev,
        sessao: {
          unidade: unitName,
          trabalhador: user.email?.split('@')[0] || 'N/A',
          abertura: openCashbox ? new Date(openCashbox.opened_at).toLocaleString('pt-BR') : 'Não aberto',
          fechamento_ou_status: openCashbox ? 'Aberto' : 'Fechado'
        }
      }));

    } catch (err) {
      console.error('Error fetching cashbox data:', err);
      setError('Erro ao carregar dados do caixa');
    } finally {
      setLoading(false);
    }
  };

  const calculateMetrics = async (cashbox: Cashbox) => {
    if (!user) return;

    try {
      const periodStart = new Date(cashbox.opened_at);
      const periodEnd = new Date();

      const { data: installmentsData, error: installmentsError } = await supabase
        .from('installments')
        .select(`
          amount,
          paid_amount,
          status,
          contract:contracts!inner(
            ruta,
            approval_status,
            unit_id
          )
        `)
        .eq('unit_id', user.unit_id)
        .neq('status', 'paid')
        .eq('contracts.ruta', selectedRuta)
        .eq('contracts.approval_status', 'approved')
        .eq('contracts.unit_id', user.unit_id);

      if (installmentsError) throw installmentsError;

      const portfolioFinal = (installmentsData || []).reduce((sum, installment) => {
        const remainingAmount = installment.amount - (installment.paid_amount || 0);
        return sum + remainingAmount;
      }, 0);

      // Fetch cash movements for the period, filtered by ruta
      const { data: movements, error: movementsError } = await supabase
        .from('cash_movements')
        .select('*')
        .eq('unit_id', user.unit_id)
        .eq('ruta', selectedRuta)
        .gte('created_at', periodStart.toISOString())
        .lte('created_at', periodEnd.toISOString());

      if (movementsError) throw movementsError;

      // Calculate cash movements by type
      const newSales = (movements || [])
        .filter(m => m.kind === 'new_sale')
        .reduce((sum, m) => sum + Math.abs(m.amount), 0);

      const renewedSales = (movements || [])
        .filter(m => m.kind === 'renewed_sale')
        .reduce((sum, m) => sum + Math.abs(m.amount), 0);

      const income = (movements || [])
        .filter(m => m.kind === 'entry')
        .reduce((sum, m) => sum + m.amount, 0);

      const expenses = (movements || [])
        .filter(m => m.kind === 'expense')
        .reduce((sum, m) => sum + Math.abs(m.amount), 0);

      const withdrawals = (movements || [])
        .filter(m => m.kind === 'withdrawal')
        .reduce((sum, m) => sum + Math.abs(m.amount), 0);

      // Calculate payments received from approved transactions only
      const { data: approvedTransactions, error: transactionsError } = await supabase
        .from('payment_transactions')
        .select(`
          installment_id,
          amount,
          late_fee,
          contract:contracts!inner(ruta)
        `)
        .eq('unit_id', user.unit_id)
        .eq('status', 'approved')
        .gte('verified_at', periodStart.toISOString())
        .lte('verified_at', periodEnd.toISOString());

      if (transactionsError) throw transactionsError;

      let totalPayments = (approvedTransactions || [])
        .filter(t => t.contract?.ruta === selectedRuta)
        .reduce((sum, t) => sum + t.amount, 0);

      let totalApprovedLateFees = (approvedTransactions || [])
        .filter(t => t.contract?.ruta === selectedRuta)
        .reduce((sum, t) => sum + (t.late_fee || 0), 0);

      // Fallback: If no approved transactions, use the old installments.paid_amount system
      if (!approvedTransactions || approvedTransactions.length === 0) {
        const { data: paidInstallments, error: paidError } = await supabase
          .from('installments')
          .select(`
            paid_amount,
            payment_date_time,
            contract:contracts!inner(ruta)
          `)
          .eq('unit_id', user.unit_id)
          .not('paid_amount', 'is', null)
          .not('payment_date_time', 'is', null)
          .gte('payment_date_time', periodStart.toISOString())
          .lte('payment_date_time', periodEnd.toISOString());

        if (paidError) {
          console.error('Error fetching paid installments:', paidError);
        } else {
          totalPayments = (paidInstallments || [])
            .filter(inst => inst.contract?.ruta === selectedRuta)
            .reduce((sum, inst) => sum + (inst.paid_amount || 0), 0);
        }
      }

      // Calculate pending verification transactions - filter by ruta
      const { data: pendingTransactions, error: pendingError } = await supabase
        .from('payment_transactions')
        .select(`
          amount,
          late_fee,
          contract:contracts!inner(ruta)
        `)
        .eq('unit_id', user.unit_id)
        .eq('status', 'pending_verification')
        .gte('registered_at', periodStart.toISOString())
        .lte('registered_at', periodEnd.toISOString());

      if (pendingError) throw pendingError;

      const totalPending = (pendingTransactions || [])
        .filter(t => t.contract?.ruta === selectedRuta)
        .reduce((sum, t) => sum + t.amount, 0);

      const totalPendingLateFees = (pendingTransactions || [])
        .filter(t => t.contract?.ruta === selectedRuta)
        .reduce((sum, t) => sum + (t.late_fee || 0), 0);

      // Calculate allocated late fees - ONLY for the selected ruta
      let totalAllocatedLateFees = 0;

      if (approvedTransactions && approvedTransactions.length > 0) {
        const rutaInstallmentIds = approvedTransactions
          .filter(t => t.contract?.ruta === selectedRuta)
          .map(t => t.installment_id)
          .filter(id => id != null);

        if (rutaInstallmentIds.length > 0) {
          const { data: allocatedFees, error: allocatedError } = await supabase
            .from('late_fee_allocations')
            .select('amount')
            .in('source_installment_id', rutaInstallmentIds);

          if (allocatedError) {
            console.error('Error fetching allocated fees:', allocatedError);
          } else {
            totalAllocatedLateFees = (allocatedFees || []).reduce((sum, a) => sum + a.amount, 0);
          }
        }
      }

      const unallocatedLateFees = totalApprovedLateFees - totalAllocatedLateFees;

      const totalSales = newSales + renewedSales;
      const collections = income + totalPayments;
      const availableCash = cashbox.initial_cash_amount - totalSales + collections - expenses - withdrawals;

      // Calculate active clients (all clients with any outstanding balance for this ruta)
      const { data: activeClientsData, error: activeClientsError } = await supabase
        .from('installments')
        .select(`
          contract:contracts!inner(
            cpf,
            ruta,
            approval_status
          )
        `)
        .eq('unit_id', user.unit_id)
        .neq('status', 'paid')
        .eq('contracts.ruta', selectedRuta)
        .eq('contracts.approval_status', 'approved')
        .eq('contracts.unit_id', user.unit_id);

      if (activeClientsError) throw activeClientsError;

      // Count unique clients (by CPF)
      const uniqueActiveClients = new Set(activeClientsData?.map(i => i.contract.cpf) || []).size;

      // Calculate new contracts since cashbox opened
      const { data: newContractsData, error: newContractsError } = await supabase
        .from('cash_movements')
        .select('contract_id')
        .eq('kind', 'new_sale')
        .eq('unit_id', user.unit_id)
        .eq('ruta', selectedRuta)
        .gte('created_at', periodStart.toISOString())
        .lte('created_at', periodEnd.toISOString());

      if (newContractsError) throw newContractsError;

      const newContractsCount = new Set(newContractsData?.map(m => m.contract_id).filter(Boolean) || []).size;

      // Calculate clients to collect since cashbox opened (unique clients, not installments)
      const { data: clientsToCollectData, error: clientsToCollectError } = await supabase
        .from('installments')
        .select(`
          contract:contracts!inner(
            cpf,
            ruta,
            approval_status
          )
        `)
        .eq('unit_id', user.unit_id)
        .eq('status', 'pending')
        .gte('due_date', periodStart.toISOString().split('T')[0])
        .eq('contracts.ruta', selectedRuta)
        .eq('contracts.approval_status', 'approved')
        .eq('contracts.unit_id', user.unit_id);

      if (clientsToCollectError) throw clientsToCollectError;

      // Count unique clients (by CPF) to collect
      const clientsToCollectCount = new Set(clientsToCollectData?.map(i => i.contract.cpf) || []).size;

      // ── 3 EXTRA PORTFOLIO METRICS ──────────────────────────────────────────
      const today = new Date().toISOString().split('T')[0];

      // 1. A receber de hoje em diante (parcelas pendentes com vencimento >= hoje)
      const { data: futureData } = await supabase
        .from('installments')
        .select(`amount, paid_amount, contract:contracts!inner(ruta, approval_status, unit_id)`)
        .eq('unit_id', user.unit_id)
        .neq('status', 'paid')
        .gte('due_date', today)
        .eq('contracts.ruta', selectedRuta)
        .eq('contracts.approval_status', 'approved')
        .eq('contracts.unit_id', user.unit_id);

      const portfolioFutureReceivable = (futureData || []).reduce((sum, i) => {
        return sum + Math.max(0, i.amount - (i.paid_amount || 0));
      }, 0);

      // 2. Inadimplentes (parcelas de 1 a 90 dias de atraso, igual à tela Inadimplentes)
      const ninetyDaysAgoOverdue = new Date();
      ninetyDaysAgoOverdue.setDate(ninetyDaysAgoOverdue.getDate() - 90);
      const ninetyDaysAgoOverdueStr = ninetyDaysAgoOverdue.toISOString().split('T')[0];

      const { data: overdueData } = await supabase
        .from('installments')
        .select(`amount, paid_amount, contract:contracts!inner(ruta, approval_status, unit_id)`)
        .eq('unit_id', user.unit_id)
        .neq('status', 'paid')
        .lt('due_date', today)
        .gt('due_date', ninetyDaysAgoOverdueStr)
        .eq('contracts.ruta', selectedRuta)
        .eq('contracts.approval_status', 'approved')
        .eq('contracts.unit_id', user.unit_id);

      const portfolioOverdue = (overdueData || []).reduce((sum, i) => {
        return sum + Math.max(0, i.amount - (i.paid_amount || 0));
      }, 0);

      // 3. Curvas (parcelas de clientes com mais de 90 dias de atraso)
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      const ninetyDaysAgoStr = ninetyDaysAgo.toISOString().split('T')[0];

      const { data: curvasData } = await supabase
        .from('installments')
        .select(`
          amount, paid_amount, due_date,
          contract:contracts!inner(cpf, ruta, approval_status, unit_id)
        `)
        .eq('unit_id', user.unit_id)
        .neq('status', 'paid')
        .lte('due_date', ninetyDaysAgoStr)
        .eq('contracts.ruta', selectedRuta)
        .eq('contracts.approval_status', 'approved')
        .eq('contracts.unit_id', user.unit_id);

      const portfolioCurvas = (curvasData || []).reduce((sum, i) => {
        return sum + Math.max(0, i.amount - (i.paid_amount || 0));
      }, 0);
      // ────────────────────────────────────────────────────────────────────────

      setCashMetrics({
        initialCash: cashbox.initial_cash_amount,
        newSales,
        renewedSales,
        totalSales,
        collections,
        income,
        expenses,
        withdrawals,
        availableCash,
        pendingVerification: totalPending,
        totalLateFees: totalApprovedLateFees + totalPendingLateFees,
        unallocatedLateFees,
        portfolioFinal,
        portfolioVariation: 0,
        portfolioInitial: 0,
        portfolioCollected: collections,
        portfolioFutureReceivable,
        portfolioOverdue,
        portfolioCurvas,
        intendedCollection: 0,
        clientsToCollect: 0,
        actualCollection: collections,
        additionalCollection: 0,
        compliance: 0,
        unitCollectionPercentage: 0,
        extraCollection: 0,
        unscheduledClients: 0,
        creditsToCollect: clientsToCollectCount,
        creditsToCollectAmount: 0,
        unscheduledCredits: 0,
        unscheduledCreditsAmount: 0,
        cancelledCredits: 0,
        newCredits: newContractsCount,
        activeCredits: uniqueActiveClients,
        paidCredits: 0,
        unpaidCredits: 0,
        synchronizedCredits: 0,
        clientEfficiency: 0
      });

      // Update informacoes with calculated values
      setInformacoes(prev => ({
        ...prev,
        a_receber: clientsToCollectCount,
        quitados: 0, // TODO: Implement calculation for settled clients who didn't renew
        novos: newContractsCount,
        ativos: uniqueActiveClients
      }));

    } catch (err) {
      console.error('Error calculating metrics:', err);
    }
  };

  const fetchCashboxHistory = async () => {
    if (!user) return;

    setLoadingHistory(true);
    try {
      const { data, error } = await supabase
        .from('cashboxes')
        .select(`
          *,
          opened_by_user:opened_by_user_id(email),
          closed_by_user:closed_by_user_id(email)
        `)
        .eq('unit_id', user.unit_id)
        .eq('ruta', selectedRuta)
        .eq('status', 'closed')
        .order('closed_at', { ascending: false });

      if (error) throw error;
      setCashboxHistory(data || []);
    } catch (err) {
      console.error('Error fetching cashbox history:', err);
      setError('Erro ao carregar histórico de caixas');
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchCashboxSnapshot = async (cashboxId: string) => {
    setLoadingSnapshot(true);
    try {
      const { data, error } = await supabase
        .from('cashbox_snapshots')
        .select('*')
        .eq('cashbox_id', cashboxId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSelectedSnapshot(data as CashboxSnapshot);
        setShowSnapshotModal(true);
      } else {
        setError('Snapshot não encontrado para este caixa');
      }
    } catch (err) {
      console.error('Error fetching cashbox snapshot:', err);
      setError('Erro ao carregar detalhes do caixa');
    } finally {
      setLoadingSnapshot(false);
    }
  };

  const handleOpenCashbox = async () => {
    if (!user) return;

    setOpening(true);
    try {
      const { error } = await supabase
        .from('cashboxes')
        .insert({
          ruta: selectedRuta,
          unit_id: user.unit_id,
          opened_by_user_id: user.id,
          initial_cash_amount: parseFloat(initialCashAmount) || 0,
          status: 'open',
          mobile_start: false
        });

      if (error) throw error;

      setShowOpenModal(false);
      setInitialCashAmount('');
      await fetchCashboxData();
    } catch (err) {
      console.error('Error opening cashbox:', err);
      setError('Erro ao abrir caixa');
    } finally {
      setOpening(false);
    }
  };

  const handleCloseCashbox = async () => {
    if (!currentCashbox || !user) return;

    if (cashMetrics.unallocatedLateFees > 0) {
      const confirmed = window.confirm(
        `ATENÇÃO: Existem ${formatCurrency(cashMetrics.unallocatedLateFees)} em juros não alocados!\n\n` +
        `Estes juros devem ser alocados a clientes inadimplentes antes de fechar o caixa.\n\n` +
        `Deseja realmente fechar o caixa sem alocar os juros?`
      );
      if (!confirmed) {
        setClosing(false);
        return;
      }
    }

    setClosing(true);
    try {
      const { error: updateError } = await supabase
        .from('cashboxes')
        .update({
          status: 'closed',
          closed_at: new Date().toISOString(),
          closed_by_user_id: user.id,
          final_cash_amount: parseFloat(finalCashAmount) || cashMetrics.availableCash
        })
        .eq('id', currentCashbox.id);

      if (updateError) throw updateError;

      const { error: snapshotError } = await supabase
        .from('cashbox_snapshots')
        .insert({
          cashbox_id: currentCashbox.id,
          initial_cash: cashMetrics.initialCash,
          new_sales: cashMetrics.newSales,
          renewed_sales: cashMetrics.renewedSales,
          total_sales: cashMetrics.totalSales,
          collections: cashMetrics.collections,
          income: cashMetrics.income,
          expenses: cashMetrics.expenses,
          withdrawals: cashMetrics.withdrawals,
          available_cash: cashMetrics.availableCash,
          pending_verification: cashMetrics.pendingVerification,
          total_late_fees: cashMetrics.totalLateFees,
          unallocated_late_fees: cashMetrics.unallocatedLateFees,
          portfolio_final: cashMetrics.portfolioFinal,
          portfolio_variation: cashMetrics.portfolioVariation,
          portfolio_initial: cashMetrics.portfolioInitial,
          portfolio_collected: cashMetrics.portfolioCollected,
          intended_collection: cashMetrics.intendedCollection,
          clients_to_collect: cashMetrics.clientsToCollect,
          actual_collection: cashMetrics.actualCollection,
          additional_collection: cashMetrics.additionalCollection,
          compliance: cashMetrics.compliance,
          unit_collection_percentage: cashMetrics.unitCollectionPercentage,
          extra_collection: cashMetrics.extraCollection,
          unscheduled_clients: cashMetrics.unscheduledClients,
          credits_to_collect: cashMetrics.creditsToCollect,
          credits_to_collect_amount: cashMetrics.creditsToCollectAmount,
          unscheduled_credits: cashMetrics.unscheduledCredits,
          unscheduled_credits_amount: cashMetrics.unscheduledCreditsAmount,
          cancelled_credits: cashMetrics.cancelledCredits,
          new_credits: cashMetrics.newCredits,
          active_credits: cashMetrics.activeCredits,
          paid_credits: cashMetrics.paidCredits,
          unpaid_credits: cashMetrics.unpaidCredits,
          synchronized_credits: cashMetrics.synchronizedCredits,
          client_efficiency: cashMetrics.clientEfficiency
        });

      if (snapshotError) {
        console.error('Error creating snapshot:', snapshotError);
      }

      setShowCloseModal(false);
      setFinalCashAmount('');
      await fetchCashboxData();
    } catch (err) {
      console.error('Error closing cashbox:', err);
      setError('Erro ao fechar caixa');
    } finally {
      setClosing(false);
    }
  };

  const prepareOpenCashbox = async () => {
    if (!user) return;

    try {
      // Get the last closed cashbox to determine initial amount
      const { data: lastCashbox, error } = await supabase
        .from('cashboxes')
        .select('final_cash_amount')
        .eq('unit_id', user.unit_id)
        .eq('ruta', selectedRuta)
        .eq('status', 'closed')
        .order('closed_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      const suggestedAmount = lastCashbox?.final_cash_amount || 0;
      setInitialCashAmount(suggestedAmount.toString());
      setShowOpenModal(true);
    } catch (err) {
      console.error('Error preparing cashbox opening:', err);
      setInitialCashAmount('0');
      setShowOpenModal(true);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const handleAddExpense = async () => {
    if (!user || !currentCashbox) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('cash_movements')
        .insert({
          kind: 'expense',
          amount: -Math.abs(parseFloat(expenseAmount)),
          description: expenseDescription,
          unit_id: user.unit_id,
          user_id: user.id,
          ruta: selectedRuta,
          created_at: new Date().toISOString()
        });

      if (error) throw error;

      setShowExpenseModal(false);
      setExpenseAmount('');
      setExpenseDescription('');
      await fetchCashboxData();
    } catch (err) {
      console.error('Error adding expense:', err);
      setError('Erro ao adicionar gasto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddWithdrawal = async () => {
    if (!user || !currentCashbox) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('cash_movements')
        .insert({
          kind: 'withdrawal',
          amount: -Math.abs(parseFloat(withdrawalAmount)),
          description: withdrawalDescription,
          unit_id: user.unit_id,
          user_id: user.id,
          ruta: selectedRuta,
          created_at: new Date().toISOString()
        });

      if (error) throw error;

      setShowWithdrawalModal(false);
      setWithdrawalAmount('');
      setWithdrawalDescription('');
      await fetchCashboxData();
    } catch (err) {
      console.error('Error adding withdrawal:', err);
      setError('Erro ao adicionar retirada');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDuration = (openedAt: string, closedAt?: string) => {
    const start = new Date(openedAt);
    const end = closedAt ? new Date(closedAt) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${diffHours}h ${diffMinutes}m`;
  };

  const fetchOverdueClients = async () => {
    if (!user) return;

    setLoadingOverdueClients(true);
    try {
      console.log('🔍 Buscando clientes inadimplentes da Ruta', selectedRuta);

      const { data: installments, error: installmentsError } = await supabase
        .from('installments')
        .select(`
          id,
          due_date,
          amount,
          status,
          paid_amount,
          contract:contracts(
            id,
            client_name,
            cpf,
            code,
            ruta,
            approval_status,
            outstanding_balance
          )
        `)
        .eq('unit_id', user.unit_id)
        .neq('status', 'paid');

      if (installmentsError) throw installmentsError;

      console.log(`📊 Total de parcelas não pagas encontradas: ${installments?.length || 0}`);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      console.log(`📅 Data de hoje: ${today.toISOString()}`);

      const overdueClientsMap: { [key: string]: any } = {};
      let totalInstallmentsProcessed = 0;
      let overdueInstallmentsFound = 0;

      installments?.forEach(installment => {
        totalInstallmentsProcessed++;

        if (installment.contract.approval_status !== 'approved') {
          return;
        }

        if (installment.contract.ruta !== selectedRuta) {
          return;
        }

        const dueDate = new Date(installment.due_date);
        dueDate.setHours(23, 59, 59, 999);

        const isOverdue = dueDate < today;

        if (!isOverdue) {
          return;
        }

        const overdueDays = Math.ceil((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));

        if (overdueDays <= 5) {
          return;
        }

        const remainingAmount = installment.amount - (installment.paid_amount || 0);

        if (remainingAmount <= 0) {
          return;
        }

        overdueInstallmentsFound++;

        const clientKey = installment.contract.cpf;

        if (!overdueClientsMap[clientKey]) {
          overdueClientsMap[clientKey] = {
            id: installment.contract.id,
            client_name: installment.contract.client_name,
            cpf: installment.contract.cpf,
            code: installment.contract.code,
            outstanding_balance: installment.contract.outstanding_balance || 0,
            totalOverdue: 0,
            overdue_days: 0,
            overdueInstallments: [],
            contracts: new Set([installment.contract.code])
          };
        }

        overdueClientsMap[clientKey].totalOverdue += remainingAmount;
        overdueClientsMap[clientKey].overdue_days = Math.max(
          overdueClientsMap[clientKey].overdue_days,
          overdueDays
        );
        overdueClientsMap[clientKey].overdueInstallments.push({
          id: installment.id,
          due_date: installment.due_date,
          amount: installment.amount,
          paid_amount: installment.paid_amount,
          remaining_amount: remainingAmount,
          days_overdue: overdueDays
        });
        overdueClientsMap[clientKey].contracts.add(installment.contract.code);
      });

      console.log(`\n📊 RESUMO DA ANÁLISE:`);
      console.log(`   Total de parcelas processadas: ${totalInstallmentsProcessed}`);
      console.log(`   Parcelas vencidas com mais de 5 dias e valor pendente: ${overdueInstallmentsFound}`);
      console.log(`   Clientes únicos inadimplentes na Ruta ${selectedRuta}: ${Object.keys(overdueClientsMap).length}`);

      const overdueClientsList = Object.values(overdueClientsMap)
        .map(client => ({
          ...client,
          contracts: Array.from(client.contracts)
        }))
        .sort((a, b) => {
          if (b.overdue_days !== a.overdue_days) {
            return b.overdue_days - a.overdue_days;
          }
          return b.totalOverdue - a.totalOverdue;
        });

      setOverdueClients(overdueClientsList);
    } catch (err) {
      console.error('❌ Erro ao carregar clientes inadimplentes:', err);
      setError('Erro ao carregar clientes inadimplentes');
    } finally {
      setLoadingOverdueClients(false);
    }
  };

  const handleOpenAllocateModal = () => {
    if (cashMetrics.unallocatedLateFees <= 0) {
      alert('Não há juros disponíveis para alocar.');
      return;
    }
    setAllocationAmount(cashMetrics.unallocatedLateFees.toFixed(2));
    fetchOverdueClients();
    setShowAllocateModal(true);
  };

  const fetchNewSalesDetails = async () => {
    if (!user || !currentCashbox) return;

    setLoadingDetails(true);
    try {
      const periodStart = new Date(currentCashbox.opened_at);
      const periodEnd = new Date();

      const { data: movements, error } = await supabase
        .from('cash_movements')
        .select(`
          id,
          amount,
          created_at,
          contract:contracts(
            id,
            client_name,
            code
          ),
          user:users(
            email
          )
        `)
        .eq('kind', 'new_sale')
        .eq('unit_id', user.unit_id)
        .eq('ruta', selectedRuta)
        .gte('created_at', periodStart.toISOString())
        .lte('created_at', periodEnd.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      const details = (movements || [])
        .map(m => ({
        id: m.id,
        contract_id: m.contract.id,
        client_name: m.contract.client_name,
        contract_code: m.contract.code,
        amount: Math.abs(m.amount),
        created_at: m.created_at,
        seller_email: m.user?.email || 'Sistema',
        kind: 'new_sale' as const
      }));

      setNewSalesDetails(details);
      setShowNewSalesModal(true);
    } catch (err) {
      console.error('Error fetching new sales details:', err);
      setError('Erro ao carregar detalhes de vendas novas');
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchRenewedSalesDetails = async () => {
    if (!user || !currentCashbox) return;

    setLoadingDetails(true);
    try {
      const periodStart = new Date(currentCashbox.opened_at);
      const periodEnd = new Date();

      const { data: movements, error } = await supabase
        .from('cash_movements')
        .select(`
          id,
          amount,
          created_at,
          contract:contracts(
            id,
            client_name,
            code
          ),
          user:users(
            email
          )
        `)
        .eq('kind', 'renewed_sale')
        .eq('unit_id', user.unit_id)
        .eq('ruta', selectedRuta)
        .gte('created_at', periodStart.toISOString())
        .lte('created_at', periodEnd.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      const details = (movements || [])
        .map(m => ({
        id: m.id,
        contract_id: m.contract.id,
        client_name: m.contract.client_name,
        contract_code: m.contract.code,
        amount: Math.abs(m.amount),
        created_at: m.created_at,
        seller_email: m.user?.email || 'Sistema',
        kind: 'renewed_sale' as const
      }));

      setRenewedSalesDetails(details);
      setShowRenewedSalesModal(true);
    } catch (err) {
      console.error('Error fetching renewed sales details:', err);
      setError('Erro ao carregar detalhes de vendas renovadas');
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchPaymentsDetails = async () => {
    if (!user || !currentCashbox) return;

    setLoadingDetails(true);
    try {
      const periodStart = new Date(currentCashbox.opened_at);
      const periodEnd = new Date();

      const { data: transactions, error } = await supabase
        .from('payment_transactions')
        .select(`
          id,
          amount,
          late_fee,
          status,
          registered_at,
          verified_at,
          installment:installments!inner(
            due_date,
            contract:contracts!inner(
              client_name,
              code,
              ruta
            )
          )
        `)
        .eq('unit_id', user.unit_id)
        .eq('status', 'approved')
        .gte('verified_at', periodStart.toISOString())
        .lte('verified_at', periodEnd.toISOString())
        .order('verified_at', { ascending: false });

      if (error) throw error;

      const details = (transactions || [])
        .filter(t => t.installment?.contract?.ruta === selectedRuta)
        .map(t => ({
          id: t.id,
          client_name: t.installment.contract.client_name,
          contract_code: t.installment.contract.code,
          installment_number: 0,
          amount: t.amount,
          late_fee: t.late_fee || 0,
          registered_at: t.registered_at,
          verified_at: t.verified_at,
          status: t.status
        }));

      setPaymentsDetails(details);
      setShowPaymentsModal(true);
    } catch (err) {
      console.error('Error fetching payments details:', err);
      setError('Erro ao carregar detalhes de recebimentos');
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchIncomeDetails = async () => {
    if (!user || !currentCashbox) return;

    setLoadingDetails(true);
    try {
      const periodStart = new Date(currentCashbox.opened_at);
      const periodEnd = new Date();

      const { data: movements, error } = await supabase
        .from('cash_movements')
        .select(`
          id,
          amount,
          description,
          created_at,
          user:users(email)
        `)
        .eq('kind', 'entry')
        .eq('unit_id', user.unit_id)
        .eq('ruta', selectedRuta)
        .gte('created_at', periodStart.toISOString())
        .lte('created_at', periodEnd.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      const details = (movements || [])
        .map(m => ({
        id: m.id,
        description: m.description || 'Entrada extra',
        amount: m.amount,
        created_at: m.created_at,
        user_email: m.user.email
      }));

      setIncomeDetails(details);
      setShowIncomeModal(true);
    } catch (err) {
      console.error('Error fetching income details:', err);
      setError('Erro ao carregar detalhes de entradas extras');
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchLateFeesDetails = async () => {
    if (!user || !currentCashbox) return;

    setLoadingDetails(true);
    try {
      const periodStart = new Date(currentCashbox.opened_at);
      const periodEnd = new Date();

      const { data: transactions, error } = await supabase
        .from('payment_transactions')
        .select(`
          id,
          late_fee,
          verified_at,
          installment:installments!inner(
            id,
            contract:contracts!inner(
              client_name,
              code,
              ruta
            )
          )
        `)
        .eq('unit_id', user.unit_id)
        .eq('status', 'approved')
        .gt('late_fee', 0)
        .gte('verified_at', periodStart.toISOString())
        .lte('verified_at', periodEnd.toISOString())
        .order('verified_at', { ascending: false });

      if (error) throw error;

      const filtered = (transactions || [])
        .filter(t => t.installment?.contract?.ruta === selectedRuta);

      // Batch-fetch all allocations for visible transactions in one query
      const filteredInstallmentIds = filtered.map(t => t.installment.id);
      const { data: allAllocations } = filteredInstallmentIds.length > 0
        ? await supabase
            .from('late_fee_allocations')
            .select(`
              source_installment_id,
              target_contract:contracts!target_contract_id(
                client_name,
                code
              )
            `)
            .in('source_installment_id', filteredInstallmentIds)
        : { data: [] };

      const allocationByInstallment = new Map(
        (allAllocations || []).map(a => [a.source_installment_id, a])
      );

      const details: LateFeeDetail[] = filtered.map(t => {
        const allocation = allocationByInstallment.get(t.installment.id);
        return {
          id: t.id,
          client_name: t.installment.contract.client_name,
          contract_code: t.installment.contract.code,
          amount: t.late_fee,
          verified_at: t.verified_at,
          allocated: !!allocation,
          allocated_to_client: (allocation?.target_contract as { client_name?: string } | undefined)?.client_name,
          allocated_to_contract: (allocation?.target_contract as { code?: string } | undefined)?.code
        };
      });

      setLateFeesDetails(details);
      setShowLateFeesModal(true);
    } catch (err) {
      console.error('Error fetching late fees details:', err);
      setError('Erro ao carregar detalhes de juros');
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchLateFeeDebugInfo = async () => {
    if (!user || !currentCashbox) return;

    setLoadingDebug(true);
    try {
      const periodStart = new Date(currentCashbox.opened_at);
      const periodEnd = new Date();

      const { data: approvedTransactions, error: transactionsError } = await supabase
        .from('payment_transactions')
        .select(`
          id,
          installment_id,
          amount,
          late_fee,
          verified_at,
          installment:installments!inner(
            id,
            contract:contracts!inner(
              id,
              client_name,
              code,
              ruta
            )
          )
        `)
        .eq('unit_id', user.unit_id)
        .eq('status', 'approved')
        .gt('late_fee', 0)
        .gte('verified_at', periodStart.toISOString())
        .lte('verified_at', periodEnd.toISOString())
        .order('verified_at', { ascending: false });

      if (transactionsError) throw transactionsError;

      const debugData = {
        totalTransactions: approvedTransactions?.length || 0,
        selectedRuta,
        transactions: []
      };

      for (const transaction of (approvedTransactions || [])) {
        const { data: allocations } = await supabase
          .from('late_fee_allocations')
          .select(`
            id,
            amount,
            created_at,
            target_contract:contracts!target_contract_id(
              client_name,
              code
            )
          `)
          .eq('source_installment_id', transaction.installment_id);

        const totalAllocated = (allocations || []).reduce((sum, a) => sum + a.amount, 0);
        const available = transaction.late_fee - totalAllocated;
        const isRutaMatch = transaction.installment?.contract?.ruta === selectedRuta;

        debugData.transactions.push({
          id: transaction.id,
          client: transaction.installment?.contract?.client_name || 'N/A',
          contract_code: transaction.installment?.contract?.code || 'N/A',
          ruta: transaction.installment?.contract?.ruta || 'N/A',
          isRutaMatch,
          late_fee: transaction.late_fee,
          allocated: totalAllocated,
          available,
          verified_at: transaction.verified_at,
          allocations: allocations || []
        });
      }

      setDebugInfo(debugData);
      setShowDebugModal(true);
    } catch (err) {
      console.error('Error fetching debug info:', err);
      setError('Erro ao carregar informações de debug');
    } finally {
      setLoadingDebug(false);
    }
  };

  const fetchExpensesDetails = async () => {
    if (!user || !currentCashbox) return;

    setLoadingDetails(true);
    try {
      const periodStart = new Date(currentCashbox.opened_at);
      const periodEnd = new Date();

      const { data: movements, error } = await supabase
        .from('cash_movements')
        .select(`
          id,
          amount,
          description,
          created_at,
          user:users(email)
        `)
        .eq('kind', 'expense')
        .eq('unit_id', user.unit_id)
        .eq('ruta', selectedRuta)
        .gte('created_at', periodStart.toISOString())
        .lte('created_at', periodEnd.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      const details = (movements || [])
        .map(m => ({
        id: m.id,
        description: m.description || 'Gasto',
        amount: Math.abs(m.amount),
        created_at: m.created_at,
        user_email: m.user.email
      }));

      setExpensesDetails(details);
      setShowExpensesListModal(true);
    } catch (err) {
      console.error('Error fetching expenses details:', err);
      setError('Erro ao carregar detalhes de gastos');
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchWithdrawalsDetails = async () => {
    if (!user || !currentCashbox) return;

    setLoadingDetails(true);
    try {
      const periodStart = new Date(currentCashbox.opened_at);
      const periodEnd = new Date();

      const { data: movements, error } = await supabase
        .from('cash_movements')
        .select(`
          id,
          amount,
          description,
          created_at,
          user:users(email)
        `)
        .eq('kind', 'withdrawal')
        .eq('unit_id', user.unit_id)
        .eq('ruta', selectedRuta)
        .gte('created_at', periodStart.toISOString())
        .lte('created_at', periodEnd.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      const details = (movements || [])
        .map(m => ({
        id: m.id,
        description: m.description || 'Retirada',
        amount: Math.abs(m.amount),
        created_at: m.created_at,
        user_email: m.user.email
      }));

      setWithdrawalsDetails(details);
      setShowWithdrawalsListModal(true);
    } catch (err) {
      console.error('Error fetching withdrawals details:', err);
      setError('Erro ao carregar detalhes de retiradas');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleAllocateLateFee = async () => {
    if (!user || !selectedOverdueClient || !allocationAmount || !currentCashbox) {
      alert('Por favor, selecione um cliente e informe o valor.');
      return;
    }

    const amount = parseFloat(allocationAmount);
    if (amount <= 0 || amount > cashMetrics.unallocatedLateFees) {
      alert(`O valor deve estar entre R$ 0,01 e R$ ${cashMetrics.unallocatedLateFees.toFixed(2)}`);
      return;
    }

    if (selectedOverdueClient.overdue_days <= 5) {
      alert('O cliente deve estar inadimplente há mais de 5 dias para receber alocação de juros.');
      return;
    }

    if (selectedOverdueClient.totalOverdue <= 0) {
      alert('O cliente não possui saldo devedor para receber alocação de juros.');
      return;
    }

    setAllocating(true);
    try {
      const overdueInstallment = selectedOverdueClient.overdueInstallments[0];

      const { data: targetContractCheck, error: targetCheckError } = await supabase
        .from('contracts')
        .select('id, client_name, code, ruta')
        .eq('id', selectedOverdueClient.id)
        .single();

      if (targetCheckError) throw targetCheckError;

      if (targetContractCheck.ruta !== selectedRuta) {
        alert(`ERRO: O cliente ${targetContractCheck.client_name} não pertence à Ruta ${selectedRuta}. Juros só podem ser alocados a clientes da mesma ruta.`);
        setAllocating(false);
        return;
      }

      const { data: approvedTransactions, error: fetchError } = await supabase
        .from('payment_transactions')
        .select(`
          id,
          installment_id,
          late_fee,
          installment:installments!inner(
            id,
            contract:contracts!inner(
              id,
              client_name,
              code,
              ruta
            )
          )
        `)
        .eq('status', 'approved')
        .gt('late_fee', 0)
        .gte('verified_at', currentCashbox.opened_at);

      if (fetchError) throw fetchError;

      const rutaTransactions = (approvedTransactions || [])
        .filter(t => t.installment?.contract?.ruta === selectedRuta);

      if (rutaTransactions.length === 0) {
        alert('Nenhum juros aprovado encontrado para alocar nesta rota.');
        setAllocating(false);
        return;
      }

      let remainingAllocation = amount;
      const allocationsToCreate = [];

      // Batch-fetch all existing allocations for all ruta transactions in one query
      const allInstallmentIds = rutaTransactions.map(t => t.installment_id);
      const { data: allExistingAllocations } = await supabase
        .from('late_fee_allocations')
        .select('source_installment_id, amount')
        .in('source_installment_id', allInstallmentIds);

      const alreadyAllocatedByInstallment = new Map<string, number>();
      for (const a of allExistingAllocations || []) {
        alreadyAllocatedByInstallment.set(
          a.source_installment_id,
          (alreadyAllocatedByInstallment.get(a.source_installment_id) || 0) + a.amount
        );
      }

      for (const transaction of rutaTransactions) {
        if (remainingAllocation <= 0) break;

        const alreadyAllocated = alreadyAllocatedByInstallment.get(transaction.installment_id) || 0;
        const availableFromThisTransaction = transaction.late_fee - alreadyAllocated;

        if (availableFromThisTransaction > 0) {
          const allocationAmount = Math.min(availableFromThisTransaction, remainingAllocation);
          allocationsToCreate.push({
            transaction,
            amount: allocationAmount
          });
          remainingAllocation -= allocationAmount;
        }
      }

      if (allocationsToCreate.length === 0) {
        alert('Todos os juros aprovados já foram alocados. Não há juros disponíveis.');
        setAllocating(false);
        return;
      }

      if (remainingAllocation > 0.01) {
        alert(`Apenas ${formatCurrency(amount - remainingAllocation)} estão disponíveis para alocação. Ajuste o valor.`);
        setAllocating(false);
        return;
      }

      // Batch-fetch contract_ids for all installments that need allocation
      const allocationInstallmentIds = allocationsToCreate.map(a => a.transaction.installment_id);
      const { data: installmentContracts, error: sourceError } = await supabase
        .from('installments')
        .select('id, contract_id')
        .in('id', allocationInstallmentIds);

      if (sourceError) throw sourceError;

      const contractIdByInstallment = new Map(
        (installmentContracts || []).map(i => [i.id, i.contract_id])
      );

      const allocationInserts = allocationsToCreate.map(allocation => ({
        source_contract_id: contractIdByInstallment.get(allocation.transaction.installment_id),
        source_installment_id: allocation.transaction.installment_id,
        target_contract_id: selectedOverdueClient.id,
        target_installment_id: overdueInstallment.id,
        amount: allocation.amount
      }));

      const { error: allocationError } = await supabase
        .from('late_fee_allocations')
        .insert(allocationInserts);

      if (allocationError) throw allocationError;

      const sourceTransaction = allocationsToCreate[0].transaction;

      const remainingAmount = overdueInstallment.amount - (overdueInstallment.paid_amount || 0);
      const newPaidAmount = Math.min(
        overdueInstallment.amount,
        (overdueInstallment.paid_amount || 0) + amount
      );
      const newStatus = newPaidAmount >= overdueInstallment.amount ? 'paid' : 'partial';

      const { error: installmentError } = await supabase
        .from('installments')
        .update({
          paid_amount: newPaidAmount,
          status: newStatus
        })
        .eq('id', overdueInstallment.id);

      if (installmentError) throw installmentError;

      const { data: contractData, error: contractFetchError } = await supabase
        .from('contracts')
        .select('outstanding_balance')
        .eq('id', selectedOverdueClient.id)
        .single();

      if (contractFetchError) throw contractFetchError;

      const newOutstandingBalance = Math.max(0, contractData.outstanding_balance - amount);

      const { error: contractError } = await supabase
        .from('contracts')
        .update({ outstanding_balance: newOutstandingBalance })
        .eq('id', selectedOverdueClient.id);

      if (contractError) throw contractError;

      const { error: transactionError } = await supabase
        .from('payment_transactions')
        .insert({
          installment_id: overdueInstallment.id,
          contract_id: selectedOverdueClient.id,
          amount: amount,
          late_fee: 0,
          bank_name: 'ALOCACAO FINANCEIRA',
          proof_file_url: [],
          payment_date_time: new Date().toISOString(),
          registered_by: user.id,
          registered_at: new Date().toISOString(),
          status: 'approved',
          verified_by: user.id,
          verified_at: new Date().toISOString(),
          unit_id: user.unit_id,
          allocation_source_type: 'interest_allocation',
          allocated_by_user_id: user.id,
          allocation_from_cashbox_id: currentCashbox.id,
          allocation_metadata: {
            allocation_type: 'interest_allocation',
            source_transaction_id: sourceTransaction.id,
            source_contract_id: sourceContract.contract_id,
            source_contract_code: sourceTransaction.installment.contract.code,
            source_client_name: sourceTransaction.installment.contract.client_name,
            target_contract_code: targetContractCheck.code,
            target_client_name: targetContractCheck.client_name,
            late_fee_allocation_id: allocationRecord.id,
            allocated_at: new Date().toISOString(),
            allocated_by_email: user.email,
            cashbox_ruta: selectedRuta,
            overdue_days: selectedOverdueClient.overdue_days,
            allocation_reason: `Alocacao de juros do caixa Ruta ${selectedRuta} para cliente inadimplente`
          }
        });

      if (transactionError) throw transactionError;

      alert(`Juros alocados com sucesso!\n\nCliente: ${selectedOverdueClient.client_name}\nValor: ${formatCurrency(amount)}\nNovo saldo devedor: ${formatCurrency(newOutstandingBalance)}\n\nEsta alocação aparecerá no Controle de PIX como uma baixa de pagamento com identificação de alocação financeira.`);

      setShowAllocateModal(false);
      setSelectedOverdueClient(null);
      setAllocationAmount('');
      await fetchCashboxData();
    } catch (err) {
      console.error('Error allocating late fee:', err);
      alert('Erro ao alocar juros. Por favor, tente novamente.');
    } finally {
      setAllocating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
          <p className="text-sm text-gray-500">Carregando caixa...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* ── PAGE HEADER ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Caixa</h1>
            <p className="text-sm text-gray-500 mt-0.5">Controle financeiro da sessão atual</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selectedRuta}
              onChange={(e) => setSelectedRuta(parseInt(e.target.value))}
              className="h-9 pl-3 pr-8 border border-gray-200 rounded-lg text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
            >
              {getVisibleRutas(user).map(r => (
                <option key={r} value={r}>Ruta {r}</option>
              ))}
            </select>
            <button
              onClick={() => setShowHistoryModal(true)}
              className="h-9 flex items-center gap-2 px-4 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
            >
              <Calendar className="w-4 h-4" />
              Ver Histórico
            </button>
            {currentCashbox ? (
              <button
                onClick={() => {
                  setFinalCashAmount(cashMetrics.availableCash.toString());
                  setShowCloseModal(true);
                }}
                className="h-9 flex items-center gap-2 px-4 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 active:scale-95 transition-all shadow-sm"
              >
                <X className="w-4 h-4" />
                Fechar Caixa
              </button>
            ) : (
              <button
                onClick={prepareOpenCashbox}
                className="h-9 flex items-center gap-2 px-4 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 active:scale-95 transition-all shadow-sm"
              >
                <Plus className="w-4 h-4" />
                Abrir Caixa
              </button>
            )}
          </div>
        </div>

        {/* ── ERROR BANNER ── */}
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* ── SESSION STATUS CARD ── */}
        {currentCashbox ? (
          <div className="bg-white rounded-2xl border border-blue-100 shadow-sm p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-emerald-700">Caixa Aberto</span>
                    <span className="text-xs text-gray-400">·</span>
                    <span className="text-sm text-gray-600">Ruta {currentCashbox.ruta}</span>
                    {currentCashbox.mobile_start && (
                      <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">Mobile</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Aberto em {new Date(currentCashbox.opened_at).toLocaleDateString('pt-BR')} às {new Date(currentCashbox.opened_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-6 sm:gap-8">
                <div className="text-center">
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Caixa Inicial</p>
                  <p className="text-base font-bold text-gray-800 mt-0.5">{formatCurrency(currentCashbox.initial_cash_amount)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Duração</p>
                  <p className="text-base font-bold text-gray-800 mt-0.5">{formatDuration(currentCashbox.opened_at)}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-400 uppercase tracking-wide">Unidade</p>
                  <p className="text-base font-bold text-gray-800 mt-0.5">{unitName || informacoes.sessao.unidade || '—'}</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-amber-100 shadow-sm p-5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <div className="w-3 h-3 rounded-full bg-amber-400" />
              </div>
              <div>
                <span className="text-sm font-semibold text-amber-700">Caixa Fechado</span>
                <p className="text-xs text-gray-500 mt-0.5">
                  Nenhum caixa aberto para a Ruta {selectedRuta}. Clique em "Abrir Caixa" para iniciar uma nova sessão.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── MAIN BALANCE CARD ── */}
        <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${currentCashbox ? 'border-gray-100' : 'border-gray-100 opacity-60'}`}>
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-start justify-between mb-6">
              <div>
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Caixa Atual</p>
                <p className="text-xs text-gray-400 mt-0.5">{new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-400">BRL</span>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
              <div>
                <p className={`text-5xl font-black tracking-tight ${cashMetrics.availableCash >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                  {formatCurrency(cashMetrics.availableCash)}
                </p>
                <p className="text-sm text-gray-400 mt-2">Saldo disponível em caixa</p>
              </div>
              {currentCashbox && (
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowExpenseModal(true)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-red-50 text-red-700 border border-red-200 rounded-xl text-sm font-semibold hover:bg-red-100 transition-all active:scale-95"
                  >
                    <Minus className="w-4 h-4" />
                    Gasto
                  </button>
                  <button
                    onClick={() => setShowWithdrawalModal(true)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-orange-50 text-orange-700 border border-orange-200 rounded-xl text-sm font-semibold hover:bg-orange-100 transition-all active:scale-95"
                  >
                    <TrendingDown className="w-4 h-4" />
                    Retirada
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Pending verification notice */}
          {cashMetrics.pendingVerification > 0 && (
            <div className="mx-6 mb-4 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-700">
                <span className="font-semibold">{formatCurrency(cashMetrics.pendingVerification)}</span> aguardando verificação — não incluídos no saldo atual
              </p>
            </div>
          )}
        </div>

        {/* ── FLOW GRID: ENTRADAS / SAÍDAS / CARTEIRA ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* ENTRADAS */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Entradas</h2>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between items-center py-2.5 px-3 rounded-lg hover:bg-gray-50 transition-colors">
                <span className="text-sm text-gray-600">Caixa Inicial</span>
                <span className="text-sm font-semibold text-gray-800">{formatCurrency(cashMetrics.initialCash)}</span>
              </div>
              <button
                onClick={() => currentCashbox && fetchPaymentsDetails()}
                disabled={!currentCashbox}
                className="w-full flex justify-between items-center py-2.5 px-3 rounded-lg hover:bg-emerald-50 transition-colors group disabled:opacity-50 disabled:cursor-default"
              >
                <span className="text-sm text-gray-600 flex items-center gap-1.5">
                  Recebimentos
                  {currentCashbox && <Eye className="w-3 h-3 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />}
                </span>
                <span className="text-sm font-semibold text-emerald-600">+{formatCurrency(cashMetrics.collections)}</span>
              </button>
              <button
                onClick={() => currentCashbox && fetchIncomeDetails()}
                disabled={!currentCashbox}
                className="w-full flex justify-between items-center py-2.5 px-3 rounded-lg hover:bg-emerald-50 transition-colors group disabled:opacity-50 disabled:cursor-default"
              >
                <span className="text-sm text-gray-600 flex items-center gap-1.5">
                  Entradas Extras
                  {currentCashbox && <Eye className="w-3 h-3 text-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity" />}
                </span>
                <span className="text-sm font-semibold text-emerald-600">+{formatCurrency(cashMetrics.income)}</span>
              </button>
              <button
                onClick={() => currentCashbox && fetchNewSalesDetails()}
                disabled={!currentCashbox}
                className="w-full flex justify-between items-center py-2.5 px-3 rounded-lg hover:bg-red-50 transition-colors group disabled:opacity-50 disabled:cursor-default"
              >
                <span className="text-sm text-gray-600 flex items-center gap-1.5">
                  Novas Vendas
                  {currentCashbox && <Eye className="w-3 h-3 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" />}
                </span>
                <span className="text-sm font-semibold text-red-500">-{formatCurrency(cashMetrics.newSales)}</span>
              </button>
              <button
                onClick={() => currentCashbox && fetchRenewedSalesDetails()}
                disabled={!currentCashbox}
                className="w-full flex justify-between items-center py-2.5 px-3 rounded-lg hover:bg-red-50 transition-colors group disabled:opacity-50 disabled:cursor-default"
              >
                <span className="text-sm text-gray-600 flex items-center gap-1.5">
                  Vendas Renovadas
                  {currentCashbox && <Eye className="w-3 h-3 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" />}
                </span>
                <span className="text-sm font-semibold text-red-500">-{formatCurrency(cashMetrics.renewedSales)}</span>
              </button>
              <div className="pt-3 mt-1 border-t border-gray-100 flex justify-between items-center px-3">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Vendas</span>
                <span className="text-sm font-bold text-red-600">-{formatCurrency(cashMetrics.totalSales)}</span>
              </div>

              {/* Late fees */}
              <div className="mt-3 pt-3 border-t border-gray-100">
                <button
                  onClick={() => currentCashbox && fetchLateFeesDetails()}
                  disabled={!currentCashbox}
                  className="w-full flex justify-between items-center py-2.5 px-3 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors group disabled:opacity-50 disabled:cursor-default"
                >
                  <span className="text-sm text-amber-800 font-medium flex items-center gap-1.5">
                    Juros Recebidos
                    {currentCashbox && <Eye className="w-3 h-3 text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity" />}
                  </span>
                  <span className="text-sm font-bold text-amber-700">{formatCurrency(cashMetrics.totalLateFees)}</span>
                </button>
                {cashMetrics.unallocatedLateFees > 0 && (
                  <div className="mt-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-amber-700">Não alocados</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-amber-700">{formatCurrency(cashMetrics.unallocatedLateFees)}</span>
                        {currentCashbox && (
                          <button onClick={fetchLateFeeDebugInfo} className="text-amber-500 hover:text-amber-700 transition-colors" title="Detalhes técnicos">
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-amber-600 mt-1">Alocar antes de fechar o caixa</p>
                    {currentCashbox && (
                      <button
                        onClick={handleOpenAllocateModal}
                        className="w-full mt-2 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-semibold hover:bg-amber-600 transition-colors flex items-center justify-center gap-1.5"
                      >
                        <DollarSign className="w-3.5 h-3.5" />
                        Alocar Juros
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SAÍDAS */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                <TrendingDown className="w-4 h-4 text-red-600" />
              </div>
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Saídas</h2>
            </div>
            <div className="space-y-1">
              <button
                onClick={() => currentCashbox && fetchExpensesDetails()}
                disabled={!currentCashbox}
                className="w-full flex justify-between items-center py-2.5 px-3 rounded-lg hover:bg-red-50 transition-colors group disabled:opacity-50 disabled:cursor-default"
              >
                <span className="text-sm text-gray-600 flex items-center gap-1.5">
                  Gastos
                  {currentCashbox && <Eye className="w-3 h-3 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" />}
                </span>
                <span className="text-sm font-semibold text-red-600">-{formatCurrency(cashMetrics.expenses)}</span>
              </button>
              <button
                onClick={() => currentCashbox && fetchWithdrawalsDetails()}
                disabled={!currentCashbox}
                className="w-full flex justify-between items-center py-2.5 px-3 rounded-lg hover:bg-orange-50 transition-colors group disabled:opacity-50 disabled:cursor-default"
              >
                <span className="text-sm text-gray-600 flex items-center gap-1.5">
                  Retiradas
                  {currentCashbox && <Eye className="w-3 h-3 text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity" />}
                </span>
                <span className="text-sm font-semibold text-orange-600">-{formatCurrency(cashMetrics.withdrawals)}</span>
              </button>
              <div className="pt-3 mt-1 border-t border-gray-100 flex justify-between items-center px-3">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Saídas</span>
                <span className="text-sm font-bold text-red-700">-{formatCurrency(cashMetrics.expenses + cashMetrics.withdrawals)}</span>
              </div>
            </div>

            {/* Divider */}
            <div className="mt-5 pt-5 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Sessão do Caixa</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Unidade</span>
                  <span className="font-medium text-gray-800">{unitName || informacoes.sessao.unidade || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Operador</span>
                  <span className="font-medium text-gray-800">{informacoes.sessao.trabalhador || user?.email?.split('@')[0] || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Abertura</span>
                  <span className="font-medium text-gray-800 text-xs">{informacoes.sessao.abertura || 'Não aberto'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status</span>
                  <span className={`font-semibold text-xs px-2 py-0.5 rounded-full ${currentCashbox ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                    {currentCashbox ? 'Aberto' : 'Fechado'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* CARTEIRA & CRÉDITOS */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Carteira & Créditos</h2>
            </div>

            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Carteira</p>
            <div className="space-y-1 mb-4">
              <div className="flex justify-between items-center py-1.5 px-3 rounded-lg">
                <span className="text-sm text-gray-600">Carteira Final</span>
                <span className="text-sm font-semibold text-gray-800">{formatCurrency(cashMetrics.portfolioFinal)}</span>
              </div>

              {/* ── 3 EXTRA METRICS ── */}
              <div className="flex justify-between items-center py-1.5 px-3 rounded-lg bg-blue-50">
                <span className="text-sm text-blue-700">A receber (hoje+)</span>
                <span className="text-sm font-semibold text-blue-700">{formatCurrency(cashMetrics.portfolioFutureReceivable)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 px-3 rounded-lg bg-red-50">
                <span className="text-sm text-red-700">Inadimplentes</span>
                <span className="text-sm font-semibold text-red-700">{formatCurrency(cashMetrics.portfolioOverdue)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 px-3 rounded-lg bg-orange-50">
                <span className="text-sm text-orange-700">Curvas (+90 dias)</span>
                <span className="text-sm font-semibold text-orange-700">{formatCurrency(cashMetrics.portfolioCurvas)}</span>
              </div>

              <div className="flex justify-between items-center py-1.5 px-3 rounded-lg">
                <span className="text-sm text-gray-600">Total Recebido</span>
                <span className="text-sm font-semibold text-emerald-600">{formatCurrency(cashMetrics.portfolioCollected)}</span>
              </div>
              <div className="flex justify-between items-center py-1.5 px-3 rounded-lg">
                <span className="text-sm text-gray-600">Variação</span>
                <span className={`text-sm font-semibold ${cashMetrics.portfolioVariation >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {cashMetrics.portfolioVariation >= 0 ? '+' : ''}{cashMetrics.portfolioVariation.toFixed(2)}%
                </span>
              </div>
              <div className="flex justify-between items-center py-1.5 px-3 rounded-lg">
                <span className="text-sm text-gray-600">Recebimento Real</span>
                <span className="text-sm font-semibold text-blue-600">{formatCurrency(cashMetrics.actualCollection)}</span>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Créditos</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500">A Receber</p>
                  <p className="text-lg font-bold text-gray-800 mt-0.5">{informacoes.a_receber}</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3">
                  <p className="text-xs text-emerald-600">Novos</p>
                  <p className="text-lg font-bold text-emerald-700 mt-0.5">{informacoes.novos}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3">
                  <p className="text-xs text-blue-600">Ativos</p>
                  <p className="text-lg font-bold text-blue-700 mt-0.5">{informacoes.ativos}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs text-gray-500">Quitados</p>
                  <p className="text-lg font-bold text-gray-700 mt-0.5">{informacoes.quitados}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Open Cashbox Modal */}
        {showOpenModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Abrir Caixa</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Ruta {selectedRuta}</p>
                </div>
                <button onClick={() => { setShowOpenModal(false); setInitialCashAmount(''); }} className="w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Valor Inicial do Caixa</label>
                  <input
                    type="number"
                    value={initialCashAmount}
                    onChange={(e) => setInitialCashAmount(e.target.value)}
                    step="0.01"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-400 mt-1">Valor sugerido baseado no último fechamento</p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => { setShowOpenModal(false); setInitialCashAmount(''); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors" disabled={opening}>Cancelar</button>
                <button onClick={handleOpenCashbox} disabled={opening} className="px-5 py-2 bg-emerald-600 text-white rounded-xl text-sm font-semibold hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
                  {opening ? 'Abrindo...' : 'Abrir Caixa'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Close Cashbox Modal */}
        {showCloseModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Fechar Caixa</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Ruta {selectedRuta}</p>
                </div>
                <button onClick={() => { setShowCloseModal(false); setFinalCashAmount(''); }} className="w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Resumo da Sessão</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Caixa Inicial</span>
                      <span className="font-semibold text-gray-800">{formatCurrency(cashMetrics.initialCash)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Saldo Calculado</span>
                      <span className={`font-bold ${cashMetrics.availableCash >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatCurrency(cashMetrics.availableCash)}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Valor Final (Contagem Manual)</label>
                  <input
                    type="number"
                    value={finalCashAmount}
                    onChange={(e) => setFinalCashAmount(e.target.value)}
                    step="0.01"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-400 mt-1">Conte o dinheiro físico e insira o valor real</p>
                </div>
                {parseFloat(finalCashAmount) !== cashMetrics.availableCash && finalCashAmount && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-sm text-amber-800 font-medium">Diferença: {formatCurrency(parseFloat(finalCashAmount) - cashMetrics.availableCash)}</p>
                    <p className="text-xs text-amber-600 mt-0.5">O valor manual difere do saldo calculado</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => { setShowCloseModal(false); setFinalCashAmount(''); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors" disabled={closing}>Cancelar</button>
                <button onClick={handleCloseCashbox} disabled={closing || !finalCashAmount} className="px-5 py-2 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
                  {closing ? 'Fechando...' : 'Fechar Caixa'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expense Modal */}
        {showExpenseModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Registrar Gasto</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Será subtraído do caixa atual</p>
                </div>
                <button onClick={() => { setShowExpenseModal(false); setExpenseAmount(''); setExpenseDescription(''); }} className="w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Descrição</label>
                  <input type="text" value={expenseDescription} onChange={(e) => setExpenseDescription(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="Ex: Combustível, Almoço" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Valor</label>
                  <input type="number" value={expenseAmount} onChange={(e) => setExpenseAmount(e.target.value)} step="0.01" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="0.00" />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => { setShowExpenseModal(false); setExpenseAmount(''); setExpenseDescription(''); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors" disabled={submitting}>Cancelar</button>
                <button onClick={handleAddExpense} disabled={submitting || !expenseAmount || !expenseDescription} className="px-5 py-2 bg-red-600 text-white rounded-xl text-sm font-semibold hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
                  {submitting ? 'Registrando...' : 'Registrar Gasto'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Withdrawal Modal */}
        {showWithdrawalModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Registrar Retirada</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Será subtraído do caixa atual</p>
                </div>
                <button onClick={() => { setShowWithdrawalModal(false); setWithdrawalAmount(''); setWithdrawalDescription(''); }} className="w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Descrição</label>
                  <input type="text" value={withdrawalDescription} onChange={(e) => setWithdrawalDescription(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="Ex: Pagamento fornecedor" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Valor</label>
                  <input type="number" value={withdrawalAmount} onChange={(e) => setWithdrawalAmount(e.target.value)} step="0.01" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" placeholder="0.00" />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => { setShowWithdrawalModal(false); setWithdrawalAmount(''); setWithdrawalDescription(''); }} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors" disabled={submitting}>Cancelar</button>
                <button onClick={handleAddWithdrawal} disabled={submitting || !withdrawalAmount || !withdrawalDescription} className="px-5 py-2 bg-orange-600 text-white rounded-xl text-sm font-semibold hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
                  {submitting ? 'Registrando...' : 'Registrar Retirada'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* History Modal */}
        {showHistoryModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-100 flex items-center justify-between px-6 py-4 z-10 rounded-t-2xl">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Histórico de Caixas</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Ruta {selectedRuta}</p>
                </div>
                <button onClick={() => setShowHistoryModal(false)} className="w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              <div className="p-6">

              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : cashboxHistory.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg">Nenhum histórico encontrado</p>
                  <p className="text-sm">Não há caixas fechados para esta ruta.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data de Abertura
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data de Fechamento
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Duração
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Caixa Inicial
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Caixa Final
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Variação
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Aberto Por
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fechado Por
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Origem
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {cashboxHistory.map((cashbox) => {
                        const variation = (cashbox.final_cash_amount || 0) - cashbox.initial_cash_amount;
                        return (
                          <tr key={cashbox.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(cashbox.opened_at).toLocaleDateString('pt-BR')}
                              <br />
                              <span className="text-xs text-gray-500">
                                {new Date(cashbox.opened_at).toLocaleTimeString('pt-BR')}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cashbox.closed_at ? (
                                <>
                                  {new Date(cashbox.closed_at).toLocaleDateString('pt-BR')}
                                  <br />
                                  <span className="text-xs text-gray-500">
                                    {new Date(cashbox.closed_at).toLocaleTimeString('pt-BR')}
                                  </span>
                                </>
                              ) : (
                                'N/A'
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cashbox.closed_at ? formatDuration(cashbox.opened_at, cashbox.closed_at) : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(cashbox.initial_cash_amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cashbox.final_cash_amount ? formatCurrency(cashbox.final_cash_amount) : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span className={variation >= 0 ? 'text-green-600' : 'text-red-600'}>
                                {variation >= 0 ? '+' : ''}{formatCurrency(variation)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cashbox.opened_by_user?.email || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cashbox.closed_by_user?.email || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cashbox.mobile_start ? 'Mobile' : 'Desktop'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <button
                                onClick={() => fetchCashboxSnapshot(cashbox.id)}
                                disabled={loadingSnapshot}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                              >
                                <Eye className="w-4 h-4" />
                                Ver Detalhes
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              </div>
            </div>
          </div>
        )}

        {/* Allocate Late Fee Modal */}
        {showAllocateModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-100 flex items-center justify-between px-6 py-4 rounded-t-2xl z-10">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Alocar Juros</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Transferir juros para cliente inadimplente</p>
                </div>
                <button
                  onClick={() => { setShowAllocateModal(false); setSelectedOverdueClient(null); setAllocationAmount(''); }}
                  className="w-8 h-8 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                  disabled={allocating}
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
              <div className="p-6">

              <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-yellow-800">Informação Importante</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Os juros recebidos durante esta sessão do caixa serão alocados para dar baixa na dívida de um cliente inadimplente selecionado.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-600 mb-1">Total de Juros Disponíveis:</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(cashMetrics.unallocatedLateFees)}</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valor a Alocar *
                </label>
                <input
                  type="number"
                  value={allocationAmount}
                  onChange={(e) => setAllocationAmount(e.target.value)}
                  step="0.01"
                  max={cashMetrics.unallocatedLateFees}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                  disabled={allocating}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selecione o Cliente Inadimplente *
                </label>
                {loadingOverdueClients ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : overdueClients.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="font-medium">Nenhum cliente com mais de 5 dias de atraso</p>
                    <p className="text-sm">na Ruta {selectedRuta}</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm text-blue-700">
                        <strong>{overdueClients.length}</strong> cliente{overdueClients.length !== 1 ? 's' : ''} inadimplente{overdueClients.length !== 1 ? 's' : ''} encontrado{overdueClients.length !== 1 ? 's' : ''} com mais de 5 dias de atraso
                      </p>
                    </div>
                    <div className="border border-gray-300 rounded-md max-h-96 overflow-y-auto">
                      {overdueClients.map((client) => (
                        <div
                          key={client.id}
                          onClick={() => setSelectedOverdueClient(client)}
                          className={`p-3 border-b border-gray-200 cursor-pointer hover:bg-blue-50 transition-colors ${
                            selectedOverdueClient?.id === client.id ? 'bg-blue-100 border-l-4 border-l-blue-600' : ''
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{client.client_name}</p>
                              <p className="text-sm text-gray-600">CPF: {client.cpf}</p>
                              {Array.isArray(client.contracts) && client.contracts.length > 1 ? (
                                <p className="text-sm text-gray-600">
                                  {client.contracts.length} contratos: {client.contracts.join(', ')}
                                </p>
                              ) : (
                                <p className="text-sm text-gray-600">Código: {Array.isArray(client.contracts) ? client.contracts[0] : client.code}</p>
                              )}
                              <div className="flex gap-3 mt-1">
                                <p className="text-xs text-red-600 font-medium">
                                  {client.overdueInstallments.length} parcela{client.overdueInstallments.length !== 1 ? 's' : ''} em atraso
                                </p>
                                <p className="text-xs text-orange-600 font-medium">
                                  {client.overdue_days} dia{client.overdue_days !== 1 ? 's' : ''} de atraso
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600">Em Atraso</p>
                              <p className="text-lg font-bold text-red-600">
                                {formatCurrency(client.totalOverdue)}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                Saldo Total: {formatCurrency(client.outstanding_balance)}
                              </p>
                            </div>
                          </div>
                          {selectedOverdueClient?.id === client.id && (
                            <div className="mt-2 pt-2 border-t border-blue-200">
                              <CheckCircle className="w-5 h-5 text-blue-600 inline mr-2" />
                              <span className="text-sm text-blue-600 font-medium">Cliente Selecionado</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowAllocateModal(false);
                    setSelectedOverdueClient(null);
                    setAllocationAmount('');
                  }}
                  disabled={allocating}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAllocateLateFee}
                  disabled={allocating || !selectedOverdueClient || !allocationAmount}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {allocating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Alocando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Confirmar Alocação
                    </>
                  )}
                </button>
              </div>
              </div>
            </div>
          </div>
        )}

        {showNewSalesModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">Detalhes de Novas Vendas</h2>
                <button
                  onClick={() => setShowNewSalesModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : newSalesDetails.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg">Nenhuma venda nova registrada</p>
                </div>
              ) : (
                <>
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      Total de Vendas Novas: <span className="font-bold">{newSalesDetails.length}</span>
                    </p>
                    <p className="text-lg font-bold text-blue-900">
                      Valor Total: {formatCurrency(newSalesDetails.reduce((sum, s) => sum + s.amount, 0))}
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendedor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Hora</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {newSalesDetails.map((sale) => (
                          <tr key={sale.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{sale.client_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{sale.contract_code}</td>
                            <td className="px-4 py-3 text-sm font-medium text-green-600">
                              {formatCurrency(sale.amount)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{sale.seller_email}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {new Date(sale.created_at).toLocaleDateString('pt-BR')} {new Date(sale.created_at).toLocaleTimeString('pt-BR')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {showRenewedSalesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">Detalhes de Vendas Renovadas</h2>
                <button
                  onClick={() => setShowRenewedSalesModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : renewedSalesDetails.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg">Nenhuma venda renovada registrada</p>
                </div>
              ) : (
                <>
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      Total de Vendas Renovadas: <span className="font-bold">{renewedSalesDetails.length}</span>
                    </p>
                    <p className="text-lg font-bold text-blue-900">
                      Valor Total: {formatCurrency(renewedSalesDetails.reduce((sum, s) => sum + s.amount, 0))}
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendedor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Hora</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {renewedSalesDetails.map((sale) => (
                          <tr key={sale.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{sale.client_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{sale.contract_code}</td>
                            <td className="px-4 py-3 text-sm font-medium text-green-600">
                              {formatCurrency(sale.amount)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{sale.seller_email}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {new Date(sale.created_at).toLocaleDateString('pt-BR')} {new Date(sale.created_at).toLocaleTimeString('pt-BR')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {showPaymentsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">Detalhes de Recebimentos</h2>
                <button
                  onClick={() => setShowPaymentsModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : paymentsDetails.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg">Nenhum pagamento registrado</p>
                </div>
              ) : (
                <>
                  <div className="mb-4 p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-800">
                      Total de Pagamentos: <span className="font-bold">{paymentsDetails.length}</span>
                    </p>
                    <p className="text-lg font-bold text-green-900">
                      Valor Total: {formatCurrency(paymentsDetails.reduce((sum, p) => sum + p.amount, 0))}
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      Total em Juros: {formatCurrency(paymentsDetails.reduce((sum, p) => sum + p.late_fee, 0))}
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parcela</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Juros</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Hora</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paymentsDetails.map((payment) => (
                          <tr key={payment.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{payment.client_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{payment.contract_code}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">#{payment.installment_number}</td>
                            <td className="px-4 py-3 text-sm font-medium text-green-600">
                              {formatCurrency(payment.amount)}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-yellow-600">
                              {formatCurrency(payment.late_fee)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {new Date(payment.verified_at || payment.registered_at).toLocaleDateString('pt-BR')} {new Date(payment.verified_at || payment.registered_at).toLocaleTimeString('pt-BR')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {showIncomeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">Detalhes de Entradas Extras</h2>
                <button
                  onClick={() => setShowIncomeModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : incomeDetails.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg">Nenhuma entrada extra registrada</p>
                </div>
              ) : (
                <>
                  <div className="mb-4 p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-800">
                      Total de Entradas: <span className="font-bold">{incomeDetails.length}</span>
                    </p>
                    <p className="text-lg font-bold text-green-900">
                      Valor Total: {formatCurrency(incomeDetails.reduce((sum, i) => sum + i.amount, 0))}
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responsável</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Hora</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {incomeDetails.map((income) => (
                          <tr key={income.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{income.description}</td>
                            <td className="px-4 py-3 text-sm font-medium text-green-600">
                              {formatCurrency(income.amount)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{income.user_email}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {new Date(income.created_at).toLocaleDateString('pt-BR')} {new Date(income.created_at).toLocaleTimeString('pt-BR')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {showLateFeesModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">Detalhes de Juros Recebidos</h2>
                <button
                  onClick={() => setShowLateFeesModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : lateFeesDetails.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg">Nenhum juro registrado</p>
                </div>
              ) : (
                <>
                  <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      Total de Juros: <span className="font-bold">{lateFeesDetails.length}</span>
                    </p>
                    <p className="text-lg font-bold text-yellow-900">
                      Valor Total: {formatCurrency(lateFeesDetails.reduce((sum, f) => sum + f.amount, 0))}
                    </p>
                    <p className="text-sm text-yellow-700 mt-1">
                      Alocados: {lateFeesDetails.filter(f => f.allocated).length} |
                      Não Alocados: {lateFeesDetails.filter(f => !f.allocated).length}
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente Pagador</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alocado Para</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {lateFeesDetails.map((fee) => (
                          <tr key={fee.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{fee.client_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{fee.contract_code}</td>
                            <td className="px-4 py-3 text-sm font-medium text-yellow-600">
                              {formatCurrency(fee.amount)}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {fee.allocated ? (
                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                                  Alocado
                                </span>
                              ) : (
                                <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                                  Não Alocado
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {fee.allocated ? (
                                <div>
                                  <p className="font-medium">{fee.allocated_to_client}</p>
                                  <p className="text-xs text-gray-500">{fee.allocated_to_contract}</p>
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {new Date(fee.verified_at).toLocaleDateString('pt-BR')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {showExpensesListModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">Detalhes de Gastos</h2>
                <button
                  onClick={() => setShowExpensesListModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : expensesDetails.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg">Nenhum gasto registrado</p>
                </div>
              ) : (
                <>
                  <div className="mb-4 p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-800">
                      Total de Gastos: <span className="font-bold">{expensesDetails.length}</span>
                    </p>
                    <p className="text-lg font-bold text-red-900">
                      Valor Total: {formatCurrency(expensesDetails.reduce((sum, e) => sum + e.amount, 0))}
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responsável</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Hora</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {expensesDetails.map((expense) => (
                          <tr key={expense.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{expense.description}</td>
                            <td className="px-4 py-3 text-sm font-medium text-red-600">
                              {formatCurrency(expense.amount)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{expense.user_email}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {new Date(expense.created_at).toLocaleDateString('pt-BR')} {new Date(expense.created_at).toLocaleTimeString('pt-BR')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {showWithdrawalsListModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-800">Detalhes de Retiradas</h2>
                <button
                  onClick={() => setShowWithdrawalsListModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : withdrawalsDetails.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-lg">Nenhuma retirada registrada</p>
                </div>
              ) : (
                <>
                  <div className="mb-4 p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-800">
                      Total de Retiradas: <span className="font-bold">{withdrawalsDetails.length}</span>
                    </p>
                    <p className="text-lg font-bold text-red-900">
                      Valor Total: {formatCurrency(withdrawalsDetails.reduce((sum, w) => sum + w.amount, 0))}
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responsável</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Hora</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {withdrawalsDetails.map((withdrawal) => (
                          <tr key={withdrawal.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{withdrawal.description}</td>
                            <td className="px-4 py-3 text-sm font-medium text-red-600">
                              {formatCurrency(withdrawal.amount)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{withdrawal.user_email}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {new Date(withdrawal.created_at).toLocaleDateString('pt-BR')} {new Date(withdrawal.created_at).toLocaleTimeString('pt-BR')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Cashbox Snapshot Details Modal */}
      {showSnapshotModal && selectedSnapshot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-7xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Detalhes do Caixa Fechado</h2>
              <button
                onClick={() => {
                  setShowSnapshotModal(false);
                  setSelectedSnapshot(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                  Caixa Atual
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Dinheiro Inicial</p>
                    <p className="text-lg font-bold text-gray-800">{formatCurrency(selectedSnapshot.initial_cash)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Vendas Novas</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(selectedSnapshot.new_sales)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Renovações</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(selectedSnapshot.renewed_sales)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Total Vendas</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(selectedSnapshot.total_sales)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Recebimentos</p>
                    <p className="text-lg font-bold text-blue-600">{formatCurrency(selectedSnapshot.collections)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Receitas</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(selectedSnapshot.income)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Gastos</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(selectedSnapshot.expenses)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Retiradas</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(selectedSnapshot.withdrawals)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Dinheiro Disponível</p>
                    <p className="text-lg font-bold text-blue-600">{formatCurrency(selectedSnapshot.available_cash)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Pendente Verificação</p>
                    <p className="text-lg font-bold text-yellow-600">{formatCurrency(selectedSnapshot.pending_verification)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Total Juros</p>
                    <p className="text-lg font-bold text-orange-600">{formatCurrency(selectedSnapshot.total_late_fees)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Juros Não Alocados</p>
                    <p className="text-lg font-bold text-orange-600">{formatCurrency(selectedSnapshot.unallocated_late_fees)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                  Desempenho - Carteira
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Carteira Final</p>
                    <p className="text-lg font-bold text-gray-800">{formatCurrency(selectedSnapshot.portfolio_final)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Variação</p>
                    <p className={`text-lg font-bold ${selectedSnapshot.portfolio_variation >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedSnapshot.portfolio_variation >= 0 ? '+' : ''}{formatCurrency(selectedSnapshot.portfolio_variation)}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Carteira Inicial</p>
                    <p className="text-lg font-bold text-gray-800">{formatCurrency(selectedSnapshot.portfolio_initial)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Total Recebido</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(selectedSnapshot.portfolio_collected)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-green-600" />
                  Desempenho - Recebimento
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Recebimento Previsto</p>
                    <p className="text-lg font-bold text-gray-800">{formatCurrency(selectedSnapshot.intended_collection)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Clientes para Receber</p>
                    <p className="text-lg font-bold text-gray-800">{selectedSnapshot.clients_to_collect}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Recebimento Real</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(selectedSnapshot.actual_collection)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Recebimento Adicional</p>
                    <p className="text-lg font-bold text-blue-600">{formatCurrency(selectedSnapshot.additional_collection)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Compliance</p>
                    <p className={`text-lg font-bold ${selectedSnapshot.compliance >= 80 ? 'text-green-600' : selectedSnapshot.compliance >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {selectedSnapshot.compliance.toFixed(1)}%
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">% Recebimento Unidade</p>
                    <p className="text-lg font-bold text-blue-600">{selectedSnapshot.unit_collection_percentage.toFixed(1)}%</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Recebimento Extra</p>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(selectedSnapshot.extra_collection)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Clientes Não Program.</p>
                    <p className="text-lg font-bold text-gray-800">{selectedSnapshot.unscheduled_clients}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-lg p-6 border border-yellow-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-yellow-600" />
                  Informações - Créditos
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Créditos a Receber</p>
                    <p className="text-lg font-bold text-gray-800">{selectedSnapshot.credits_to_collect}</p>
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(selectedSnapshot.credits_to_collect_amount)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Créditos Não Program.</p>
                    <p className="text-lg font-bold text-orange-600">{selectedSnapshot.unscheduled_credits}</p>
                    <p className="text-xs text-gray-500 mt-1">{formatCurrency(selectedSnapshot.unscheduled_credits_amount)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Créditos Cancelados</p>
                    <p className="text-lg font-bold text-red-600">{selectedSnapshot.cancelled_credits}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Novos Créditos</p>
                    <p className="text-lg font-bold text-green-600">{selectedSnapshot.new_credits}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Créditos Ativos</p>
                    <p className="text-lg font-bold text-blue-600">{selectedSnapshot.active_credits}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Info className="w-5 h-5 text-gray-600" />
                  Informações - Movimento de Créditos
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Créditos Pagos</p>
                    <p className="text-lg font-bold text-green-600">{selectedSnapshot.paid_credits}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Créditos Não Pagos</p>
                    <p className="text-lg font-bold text-red-600">{selectedSnapshot.unpaid_credits}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Créditos Sincronizados</p>
                    <p className="text-lg font-bold text-blue-600">{selectedSnapshot.synchronized_credits}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-600 mb-1">Eficiência Clientes</p>
                    <p className={`text-lg font-bold ${selectedSnapshot.client_efficiency >= 80 ? 'text-green-600' : selectedSnapshot.client_efficiency >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {selectedSnapshot.client_efficiency.toFixed(1)}%
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowSnapshotModal(false);
                  setSelectedSnapshot(null);
                }}
                className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Debug Modal for Late Fees */}
      {showDebugModal && debugInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">
                Debug - Juros Recebidos (Rota {debugInfo.selectedRuta})
              </h2>
              <button
                onClick={() => {
                  setShowDebugModal(false);
                  setDebugInfo(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-gray-700">
                <strong>Total de Transações com Juros:</strong> {debugInfo.totalTransactions}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Rota Selecionada:</strong> {debugInfo.selectedRuta}
              </p>
              <p className="text-xs text-gray-600 mt-2">
                Esta tela mostra todas as transações aprovadas com juros no período do caixa atual.
                Apenas transações da rota {debugInfo.selectedRuta} devem ser consideradas no cálculo de "juros não alocados".
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contrato</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rota</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Match?</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Juros Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alocado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Disponível</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {debugInfo.transactions.map((transaction: any, index: number) => (
                    <tr key={transaction.id} className={transaction.isRutaMatch ? 'bg-green-50' : 'bg-red-50'}>
                      <td className="px-4 py-3 text-sm text-gray-900">{transaction.client}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{transaction.contract_code}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        <span className={transaction.isRutaMatch ? 'font-bold text-green-700' : 'font-bold text-red-700'}>
                          {transaction.ruta}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {transaction.isRutaMatch ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <X className="w-5 h-5 text-red-600" />
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {formatCurrency(transaction.late_fee)}
                      </td>
                      <td className="px-4 py-3 text-sm text-orange-600">
                        {formatCurrency(transaction.allocated)}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-green-600">
                        {formatCurrency(transaction.available)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(transaction.verified_at).toLocaleDateString('pt-BR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-100">
                  <tr>
                    <td colSpan={4} className="px-4 py-3 text-sm font-bold text-gray-700">
                      Total da Rota {debugInfo.selectedRuta}:
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900">
                      {formatCurrency(
                        debugInfo.transactions
                          .filter((t: any) => t.isRutaMatch)
                          .reduce((sum: number, t: any) => sum + t.late_fee, 0)
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-orange-600">
                      {formatCurrency(
                        debugInfo.transactions
                          .filter((t: any) => t.isRutaMatch)
                          .reduce((sum: number, t: any) => sum + t.allocated, 0)
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-green-600">
                      {formatCurrency(
                        debugInfo.transactions
                          .filter((t: any) => t.isRutaMatch)
                          .reduce((sum: number, t: any) => sum + t.available, 0)
                      )}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h3 className="text-sm font-bold text-gray-800 mb-2">Legenda:</h3>
              <ul className="text-xs text-gray-700 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-50 border border-green-200 rounded"></div>
                  <span>Linhas verdes: Transação da rota selecionada (incluída no cálculo)</span>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-50 border border-red-200 rounded"></div>
                  <span>Linhas vermelhas: Transação de outra rota (NÃO incluída no cálculo)</span>
                </li>
                <li className="mt-2">
                  <strong>Juros Disponível:</strong> Valor que pode ser alocado (Juros Total - Alocado)
                </li>
              </ul>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowDebugModal(false);
                  setDebugInfo(null);
                }}
                className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}